import { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useModal } from "@stadium/hooks/lib/use-modal";

import Product from "@sharedComponents/cards/product";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import ProductListingHeader from "@components/store/products/listing-header";
import ProductDetailsModal from "@components/store/product-details-modal";
import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import ByoBoxIsland from "@components/store/product-detail/byo/byo-box-island";
import Portal from "@stadium/ui/src/components/portal";

import useSearchProductList from "@hooks/search/use-fetch-base-product-list";
import useSetFilters from "@hooks/search/use-set-filter";

import { handleQuantityOfProductInBox, removeProduct } from "@helpers/orders/byo-box";
import { getCanBuyProduct } from "@helpers/product-details";

import { getCountry } from "@store/selectors/country";
import {
  getStoreID,
  getStoreParentId,
  getCommonElasticSearchStoreVariables,
} from "@store/selectors/store";

import { PRODUCT_DETAILS_MODAL } from "@constants/modal-types";
import { getProductRoute } from "@constants/routes";
import { LISTING_SECTION_REF } from "@components/store/products/listing";

import styles from "./main.module.scss";

const DEFAULT_PAGE = 1;
const PER_PAGE = 150;

const AvailableProductsList = ({ product }) => {
  const [selectedOpenProduct, setSelectedOpenProduct] = useState();
  const { openModal: openProductDetailsModal, renderModal: renderProductDetailsModal } = useModal(
    PRODUCT_DETAILS_MODAL,
    ProductDetailsModal,
  );

  const {
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    handleRoutePathForFilters,
    boxProductList,
    handleBoxProductList,
    currentSortBy,
    currentPriceRangeIds,
    currentDietaryRestrictionsIds,
    setIsFetchingProducts,
    flattenedBrandValueIds,
  } = useContext(ProductListingContext);

  const { storeBasePath } = useStoreContext();
  const { getStoreUpdatedAt } = usePreviewModeContext();

  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);

  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const { componentProductIds } = product;

  const canBuyParentProduct = getCanBuyProduct({ product });

  const getVariables = useCallback(() => {
    const variables = {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      countryCode: country.iso?.toLowerCase(),
      taxonIds: product.enabledTaxonIds.map((id) => +id),
      perPage: PER_PAGE,
      sortBy: currentSortBy,
      dietaryRestrictionIds: currentDietaryRestrictionsIds.map((id) => +id),
      priceKeys: currentPriceRangeIds,
      brandIds: flattenedBrandValueIds,
      color: currentColorOptionValueIds,
      size: currentSizeOptionValueIds,
      preFilterProductIds: componentProductIds.map((id) => +id),
      storeUpdatedAt: getStoreUpdatedAt(),
      excludeOnlyBundleComponentItems: false,
      includeProductVariants: true,
      ...commonESVariables,
    };

    variables.color = currentColorOptionValueIds;
    variables.size = currentSizeOptionValueIds;

    return variables;
  }, [
    storeParentId,
    country,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    currentSortBy,
    currentPriceRangeIds,
    currentDietaryRestrictionsIds,
    componentProductIds,
    flattenedBrandValueIds,
    getStoreUpdatedAt,
    commonESVariables,
  ]);

  const productPath = getProductRoute({ storeBasePath, productSlug: product.slug });

  const { fetchedProducts, totalProducts, getProductList, loading, aggregations } =
    useSearchProductList();

  useSetFilters({ loading, aggregations });
  useEffect(() => {
    setIsFetchingProducts(loading);
  }, [loading]);

  useEffect(() => {
    handleRoutePathForFilters(productPath);

    return () => {
      handleRoutePathForFilters(null);
    };
  }, [productPath, handleRoutePathForFilters]);

  useEffect(() => {
    getProductList({
      variables: getVariables(),
    });
  }, [
    commonESVariables,
    getVariables,
    getProductList,
    country,
    currentPriceRangeIds,
    currentDietaryRestrictionsIds,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    currentSortBy,
  ]);

  const handleOpenDetailsModal = (id) => {
    setSelectedOpenProduct(id);
    openProductDetailsModal();
  };

  const addToBox = ({ newQuantity, product, externalId }) => {
    handleQuantityOfProductInBox({
      externalId,
      newQuantity,
      product,
      boxProductList,
      handleBoxProductList,
    });
  };

  const removeFromBox = (externalId) => {
    removeProduct({ externalId, boxProductList, handleBoxProductList });
  };

  const renderProducts = () => {
    return (
      <article className={styles.rowWrapper}>
        {fetchedProducts.map((product) => (
          <div key={`product-${product.id}`} className={styles.productCard}>
            <Product
              data={product}
              key={product.id}
              source="search"
              productTileClass={styles.productTileBYO}
              productImageClass={styles.productImageBlock}
              productBrandClass={styles.productBrand}
              productNameClass={styles.productName}
              productPriceClass={styles.productPrice}
              productBottomRowClass={styles.productBottomRow}
              productTileFooterClass={styles.tileFooter}
              productQtyBlockClass={styles.qtyBlock}
              className={styles.productCardContainer}
              openProductDetailsModal={handleOpenDetailsModal}
              showAddToBox
              addToBox={addToBox}
              removeFromBox={removeFromBox}
              disabled={!canBuyParentProduct}
            />
          </div>
        ))}
      </article>
    );
  };

  return (
    <>
      <section ref={LISTING_SECTION_REF}>
        <article>
          <section className="t-bg-4">
            <div className={`${styles.pageContainer} ${styles.byoProductListing}`}>
              <ProductListingHeader
                totalProducts={totalProducts}
                currentPage={DEFAULT_PAGE}
                perPage={PER_PAGE}
                showSortDropdown
                showFilters
              />
              {renderProducts()}
              {renderProductDetailsModal({
                classes: {
                  rootClass: styles.productDetailsPrimaryModal,
                  dialogClass: styles.modalDialogProductDetails,
                },
                data: {
                  productData: fetchedProducts.find(({ id }) => id === selectedOpenProduct),
                  viewingInsideByoMedley: true,
                  showAddToBox: true,
                  showFooter: false,
                  canBuyParentProduct,
                },
              })}
            </div>
          </section>
        </article>
      </section>
      <Portal
        dataContainerId="byo-box-island-portal"
        id="byo-box-island-container"
        className={styles.byoBoxIslandPortal}
      >
        <ByoBoxIsland fetchedProducts={fetchedProducts} product={product} />
        <Portal
          dataContainerId="extra-spacing-for-byo-portal"
          id="byo-box-island-container"
          className={`${styles.extraSpacingForBYO} extraSpacingForBYO t-bg-4`}
        ></Portal>
      </Portal>
    </>
  );
};

export default AvailableProductsList;
